import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Card,
  Alert,
} from 'react-bootstrap';
import { Reservation } from '../../models/reserve';

interface ReservationDialogProps {
  show: boolean;
  reservation: Reservation | null;
  type: 'view' | 'edit' | 'delete';
  onConfirm: (reservation?: Reservation) => void;
  onClose: () => void;
}

const ReservationDialog: React.FC<ReservationDialogProps> = ({
  show,
  reservation,
  type,
  onConfirm,
  onClose,
}) => {
  const [editData, setEditData] = useState<Reservation | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [imageError, setImageError] = useState<boolean>(false);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);

  useEffect(() => {
    if (reservation && type === 'edit') {
      setEditData({ ...reservation });
    }
    // 予約データが変更されたら画像エラー状態をリセット
    setImageError(false);
  }, [reservation, type]);

  // 日付をフォーマットする関数
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
    });
  };

  // シンプルな画像URL構築（フック使用を最小限に）
  const getImageUrl = (photoPath: string | null): string | null => {
    if (!photoPath) return null;

    if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
      return photoPath;
    }

    const baseUrl =
      process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const cleanPath = photoPath.replace(/^\/+/, '');
    return `${baseUrl}/${cleanPath}`;
  };

  // 画像表示の条件判定（シンプルな変数として計算）
  const shouldShowImage =
    reservation?.photo &&
    reservation?.menuDetails?.ensoficReiki?.sessionType === '遠隔';

  const imageUrl = shouldShowImage
    ? getImageUrl(reservation?.photo || null)
    : null;

  // 日時をフォーマットする関数
  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ステータスバッジの色を決定
  const getStatusVariant = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // 支払い方法の日本語表示
  const getPaymentMethodText = (paymentMethod?: string): string => {
    switch (paymentMethod) {
      case 'bank':
        return '銀行振込';
      case 'cash':
        return '現金';
      case 'credit':
        return 'クレジットカード';
      default:
        return '未設定';
    }
  };

  // ステータスの日本語表示
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending':
        return '保留中';
      case 'confirmed':
        return '確定';
      case 'completed':
        return '完了';
      case 'cancelled':
        return 'キャンセル';
      default:
        return '不明';
    }
  };

  // バリデーション
  const validateForm = (): boolean => {
    if (!editData) return false;
    return editData.name.trim() !== '' && editData.email.trim() !== '';
  };

  const handleConfirm = (): void => {
    if (type === 'edit') {
      if (editData && validateForm()) {
        onConfirm(editData);
      }
    } else {
      onConfirm();
    }
  };

  const handleInputChange = (field: keyof Reservation, value: any): void => {
    if (editData) {
      const updated = { ...editData, [field]: value };
      setEditData(updated);
      setIsValid(validateForm());
    }
  };

  const renderMenuDetails = (menuDetails: any): React.ReactNode => {
    if (!menuDetails || Object.keys(menuDetails).length === 0) {
      return <span className="text-muted">詳細なし</span>;
    }

    return (
      <div>
        {menuDetails.crystalHealing && (
          <div>
            <strong>クリスタルヒーリング:</strong>
            <br />
            セッション回数: {menuDetails.crystalHealing.sessions}回
            <br />
            施術部位: {menuDetails.crystalHealing.areas?.join(', ') || 'なし'}
          </div>
        )}
        {menuDetails.ensoficReiki && (
          <div>
            <strong>エンソフィックレイキ:</strong>
            <br />
            セッション種類: {menuDetails.ensoficReiki.sessionType}
            <br />
            ヒーリング種類: {menuDetails.ensoficReiki.healingType}
            {menuDetails.ensoficReiki.name && (
              <>
                <br />
                お名前: {menuDetails.ensoficReiki.name}
              </>
            )}
            {menuDetails.ensoficReiki.age && (
              <>
                <br />
                年齢: {menuDetails.ensoficReiki.age}歳
              </>
            )}
            {menuDetails.ensoficReiki.address && (
              <>
                <br />
                住所: {menuDetails.ensoficReiki.address}
              </>
            )}
            {menuDetails.ensoficReiki.badHabits && (
              <>
                <br />
                改善したい悪癖: {menuDetails.ensoficReiki.badHabits}
              </>
            )}
            {/* 顔写真の表示（遠隔ヒーリングの場合のみ） */}
            {imageUrl && (
              <>
                <br />
                <div className="mt-2">
                  <strong>依頼者の顔写真:</strong>
                  <div className="mt-2">
                    {!imageError ? (
                      <div>
                        <img
                          src={imageUrl}
                          alt="依頼者の顔写真"
                          style={{
                            maxWidth: '200px',
                            maxHeight: '200px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '2px solid #dee2e6',
                            cursor: 'pointer',
                          }}
                          className="img-thumbnail"
                          onError={() => setImageError(true)}
                          onLoad={() => setImageError(false)}
                          onClick={() => setShowImageModal(true)}
                          title="クリックで拡大表示"
                        />
                        <div className="mt-1 text-center">
                          <small className="text-muted">
                            🔍 クリックで拡大表示
                          </small>
                        </div>
                      </div>
                    ) : (
                      <Alert variant="warning" className="d-inline-block">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-image me-2"></i>
                          <div>
                            <strong>画像を読み込めませんでした</strong>
                            <br />
                            <small className="text-muted">
                              パス: {imageUrl}
                            </small>
                            <br />
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 mt-1"
                              onClick={() => setImageError(false)}
                            >
                              再読み込み
                            </Button>
                          </div>
                        </div>
                      </Alert>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {menuDetails.dnaActivation && (
          <div>
            <strong>DNAアクティベーション:</strong>
            <br />
            種類: {menuDetails.dnaActivation.type}
          </div>
        )}
        {menuDetails.sacredGeometry && (
          <div>
            <strong>神聖幾何学:</strong>
            <br />
            パターン: {menuDetails.sacredGeometry.pattern}
          </div>
        )}
      </div>
    );
  };

  const renderAgreementInfo = (agreements: any): React.ReactNode => {
    if (!agreements) {
      return <span className="text-muted">同意情報なし</span>;
    }

    return (
      <div>
        <Row>
          <Col md={6}>
            <strong>免責事項:</strong>
            <br />
            {agreements.disclaimerAccepted ? (
              <Badge bg="success">同意済み</Badge>
            ) : (
              <Badge bg="danger">未同意</Badge>
            )}
            {agreements.disclaimerAcceptedAt && (
              <>
                <br />
                <small className="text-muted">
                  同意日時: {formatDateTime(agreements.disclaimerAcceptedAt)}
                </small>
              </>
            )}
          </Col>
          <Col md={6}>
            <strong>誓約書:</strong>
            <br />
            {agreements.pledgeAccepted ? (
              <Badge bg="success">同意済み</Badge>
            ) : (
              <Badge bg="danger">未同意</Badge>
            )}
            {agreements.pledgeAcceptedAt && (
              <>
                <br />
                <small className="text-muted">
                  同意日時: {formatDateTime(agreements.pledgeAcceptedAt)}
                </small>
              </>
            )}
          </Col>
        </Row>
        {agreements.userAgent && (
          <div className="mt-2">
            <small className="text-muted">
              ブラウザ情報: {agreements.userAgent}
            </small>
          </div>
        )}
      </div>
    );
  };

  if (!reservation) return null;

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {type === 'view' && '予約詳細確認'}
            {type === 'edit' && '予約編集'}
            {type === 'delete' && '予約削除確認'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {type === 'delete' ? (
            <Alert variant="warning">
              <h5>削除確認</h5>
              <p>以下の予約を削除してもよろしいですか？</p>
              <p>
                <strong>予約者:</strong> {reservation.name}
                <br />
                <strong>予約日時:</strong> {formatDate(reservation.date)}{' '}
                {reservation.time}
                <br />
                <strong>メニュー:</strong> {reservation.menu}
                <br />
                <strong>支払い方法:</strong>{' '}
                {getPaymentMethodText(reservation.paymentMethod)}
              </p>
              <p className="text-danger">
                <strong>注意: この操作は取り消せません。</strong>
              </p>
            </Alert>
          ) : (
            <div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>予約者名</Form.Label>
                    {type === 'view' ? (
                      <Form.Control
                        type="text"
                        value={reservation.name}
                        readOnly
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={editData?.name || ''}
                        onChange={e =>
                          handleInputChange('name', e.target.value)
                        }
                        isInvalid={!editData?.name?.trim()}
                      />
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>メールアドレス</Form.Label>
                    {type === 'view' ? (
                      <Form.Control
                        type="email"
                        value={reservation.email}
                        readOnly
                      />
                    ) : (
                      <Form.Control
                        type="email"
                        value={editData?.email || ''}
                        onChange={e =>
                          handleInputChange('email', e.target.value)
                        }
                        isInvalid={!editData?.email?.trim()}
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>予約日</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatDate(reservation.date)}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>予約時間</Form.Label>
                    <Form.Control
                      type="text"
                      value={reservation.time}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>支払い方法</Form.Label>
                    {type === 'view' ? (
                      <div>
                        <Badge
                          bg={
                            reservation.paymentMethod === 'bank'
                              ? 'info'
                              : reservation.paymentMethod === 'cash'
                                ? 'success'
                                : reservation.paymentMethod === 'credit'
                                  ? 'primary'
                                  : 'secondary'
                          }
                          className="fs-6"
                        >
                          {getPaymentMethodText(reservation.paymentMethod)}
                        </Badge>
                      </div>
                    ) : (
                      <Form.Select
                        value={editData?.paymentMethod || ''}
                        onChange={e =>
                          handleInputChange('paymentMethod', e.target.value)
                        }
                      >
                        <option value="">選択してください</option>
                        <option value="bank">銀行振込</option>
                        <option value="cash">現金</option>
                        <option value="credit">クレジットカード</option>
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>ステータス</Form.Label>
                    <div>
                      <Badge
                        bg={getStatusVariant(reservation.status || 'pending')}
                        className="fs-6"
                      >
                        {getStatusText(reservation.status || 'pending')}
                      </Badge>
                    </div>
                    {type === 'edit' && (
                      <small className="text-muted">
                        ※ステータスの編集は現在サポートされていません
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>メニュー</Form.Label>
                    <Form.Control
                      type="text"
                      value={reservation.menu}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>金額</Form.Label>
                    <Form.Control
                      type="text"
                      value={`¥${reservation.totalPrice.toLocaleString()}`}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">メニュー詳細</h6>
                </Card.Header>
                <Card.Body>
                  {renderMenuDetails(reservation.menuDetails)}
                </Card.Body>
              </Card>

              {reservation.agreements && (
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">同意情報</h6>
                  </Card.Header>
                  <Card.Body>
                    {renderAgreementInfo(reservation.agreements)}
                  </Card.Body>
                </Card>
              )}

              {(reservation.createdAt || reservation.updatedAt) && (
                <Row>
                  {reservation.createdAt && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>作成日時</Form.Label>
                        <Form.Control
                          type="text"
                          value={formatDateTime(reservation.createdAt)}
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                  )}
                  {reservation.updatedAt && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>更新日時</Form.Label>
                        <Form.Control
                          type="text"
                          value={formatDateTime(reservation.updatedAt)}
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            {type === 'view' ? '閉じる' : 'キャンセル'}
          </Button>
          {type !== 'view' && (
            <Button
              variant={type === 'delete' ? 'danger' : 'primary'}
              onClick={handleConfirm}
              disabled={type === 'edit' && !isValid}
            >
              {type === 'delete' ? '削除' : '更新'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* 画像拡大表示モーダル */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>依頼者の顔写真</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {imageUrl && (
            <img
              src={imageUrl}
              alt="依頼者の顔写真（拡大）"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
              className="img-fluid"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReservationDialog;
